import React from "react";
import Modal from "react-modal";
import ReactLoading from "react-loading";
import { ReactComponent as Nimbus } from "../../assets/img/logo.svg";

// Variantes de encabezado. Cada una define el color de la banda (translúcido),
// el círculo del ícono y el path del ícono. Coincide con el lenguaje visual de
// los modales de avisos/notificaciones (círculo translúcido + título + cerrar).
const VARIANTS = {
  info: {
    headerBg: "bg-nimbus/5",
    iconWrap: "bg-nimbus/10 text-nimbus",
    icon: "M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z",
  },
  warning: {
    headerBg: "bg-amber-50",
    iconWrap: "bg-amber-100 text-amber-500",
    icon: "M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z",
  },
  danger: {
    headerBg: "bg-red-50",
    iconWrap: "bg-red-100 text-red-500",
    icon: "M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0",
  },
  success: {
    headerBg: "bg-green-50",
    iconWrap: "bg-green-100 text-green-600",
    icon: "M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  },
};

const NimbusModal = ({
  isOpen,
  children,
  styles = {},
  showLogo = true,
  aviso = false,
  onRequestClose,
  variant = null, // "info" | "warning" | "danger" | "success"
  title = null, // título mostrado en la banda de header
  footer = null, // contenido (botones) de la zona de footer; tiene prioridad sobre onConfirm/onCancel
  flushBody = false, // si true, el children gestiona su propio layout/scroll/padding bajo el header
  // Footer estándar: botones Cancelar/Aceptar consistentes en todos los modales.
  onConfirm = null,
  confirmText = "Aceptar",
  confirmDisabled = false,
  confirmLoading = false,
  onCancel = null,
  cancelText = "Cancelar",
  cancelDisabled = false,
}) => {
  const variantCfg = variant ? VARIANTS[variant] : null;
  const hasHeader = !!(variantCfg || title);

  // Footer estandarizado a partir de onConfirm/onCancel para que los botones se
  // vean iguales en todos los modales. El botón de confirmar es azul (nimbusDark)
  // salvo en variante "danger", donde es rojo. `footer` tiene prioridad si se
  // necesita un footer totalmente personalizado.
  const autoFooter =
    onConfirm || onCancel ? (
      <>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={cancelDisabled}
            className="px-5 py-2 rounded-lg font-semibold text-nimbusDark bg-gray-200 hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cancelText}
          </button>
        )}
        {onConfirm && (
          <button
            type="button"
            onClick={onConfirm}
            disabled={confirmDisabled || confirmLoading}
            className={`px-5 py-2 rounded-lg font-semibold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
              variant === "danger" ? "bg-red-500 hover:bg-red-600" : "bg-nimbusDark hover:bg-nimbus"
            }`}
          >
            {confirmLoading && <ReactLoading type="spin" color="#ffffff" height={16} width={16} />}
            {confirmText}
          </button>
        )}
      </>
    ) : null;
  const resolvedFooter = footer ?? autoFooter;

  const customStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      maxWidth: !aviso ? "50%" : "100%",
      // Esquinas redondeadas + sombra (estilo "Reporte de lección"), sin el
      // borde gris por defecto de react-modal.
      borderRadius: "1rem",
      border: "none",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      // En modo header gestionamos el padding internamente para que la banda
      // y el footer ocupen todo el ancho del modal.
      ...(hasHeader ? { padding: 0, overflow: "hidden" } : {}),
      ...(styles.content || {}),
    },
    overlay: {
      zIndex: 99,
      // Backdrop oscuro + blur (estilo "Reporte de lección"). Por defecto
      // react-modal usa un overlay blanco translúcido que se ve muy claro.
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      backdropFilter: "blur(4px)",
      ...(styles.overlay || {}),
    },
  };

  // --- Modo clásico (sin cambios): logo + children centrados ---
  if (!hasHeader) {
    return (
      <Modal isOpen={isOpen} style={customStyles} onRequestClose={onRequestClose} shouldCloseOnOverlayClick={!!onRequestClose}>
        <div className="relative grid text-center gap-y-4">
          {onRequestClose && (
            <button
              type="button"
              onClick={onRequestClose}
              className="absolute top-0 right-0 flex items-center justify-center w-8 h-8 rounded-full bg-nimbusDark text-white hover:bg-red-500 hover:scale-110 transition-all focus:outline-none"
              aria-label="Cerrar"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          {showLogo && <Nimbus className="justify-self-center p-1" />}
          {children}
        </div>
      </Modal>
    );
  }

  // --- Modo header: banda por variante + ícono + título, cuerpo y footer ---
  return (
    <Modal isOpen={isOpen} style={customStyles} onRequestClose={onRequestClose} shouldCloseOnOverlayClick={!!onRequestClose}>
      <div className="flex flex-col max-h-[85dvh] overflow-hidden">
        {/* Header */}
        <div className={`flex flex-shrink-0 items-center gap-3 px-5 py-4 border-b border-gray-100 ${variantCfg?.headerBg ?? "bg-nimbus/5"}`}>
          {variantCfg && (
            <span className={`flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0 ${variantCfg.iconWrap}`}>
              <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d={variantCfg.icon} />
              </svg>
            </span>
          )}
          <h1 className="flex-1 min-w-0 text-base font-bold text-nimbusDark leading-tight break-words text-left">{title}</h1>
          {onRequestClose && (
            <button
              type="button"
              onClick={onRequestClose}
              aria-label="Cerrar"
              className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Cuerpo */}
        {flushBody ? children : <div className="px-5 py-4 overflow-y-auto overscroll-contain">{children}</div>}

        {/* Footer */}
        {resolvedFooter && <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 px-5 py-4 border-t border-gray-100 flex-shrink-0">{resolvedFooter}</div>}
      </div>
    </Modal>
  );
};

export default NimbusModal;
